/**
 * El contexto del cliente HTTP y su hook.
 *
 * Vive separado de `ApiProvider.tsx` por una convención que se repite en las
 * cuatro capas del andamio (`useApi`, `useAuth`, `useTenantSettings`,
 * `useI18n`):
 *
 *   **un archivo `.tsx` exporta componentes y nada más.**
 *
 * Un módulo que exporta a la vez un componente y una constante rompe el hot
 * reload de React —el árbol se remonta entero en cada guardado y se pierde el
 * estado de la pantalla que estabas mirando— y el linter lo marca. Con el
 * contexto acá, el provider queda siendo sólo un componente.
 */
import { createContext, useContext } from 'react';

import type { ApiClient } from './client';

export const ApiContext = createContext<ApiClient | null>(null);

/**
 * El cliente HTTP. Se llama en el cuerpo del componente y se usa en el
 * `queryFn`; en la práctica lo llaman `useApiQuery` y `useKeysetQuery` por
 * vos, y una vista casi nunca lo necesita directo.
 */
export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (client === null) {
    throw new Error('useApi() fuera de <ApiProvider>. Revisá el árbol de providers.');
  }
  return client;
}
